import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Wallet, Package, ShoppingCart, Users, Factory, Briefcase, Boxes,
  FolderKanban, Settings as SettingsIcon, type LucideIcon,
} from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'

type AppEntry = {
  id: number
  code: string
  name: string
  description: string
  icon: string
  status: 'PLANNED' | 'BETA' | 'ACTIVE' | 'DEPRECATED'
  is_enabled: boolean
  display_order: number
  is_system: boolean
}

const statusBadgeStyles: Record<AppEntry['status'], string> = {
  PLANNED: 'bg-gray-100 text-gray-600',
  BETA: 'bg-amber-100 text-amber-800',
  ACTIVE: 'bg-green-100 text-green-800',
  DEPRECATED: 'bg-red-100 text-red-700',
}

// Map app code (or fallback) → lucide icon
const ICON_BY_CODE: Record<string, LucideIcon> = {
  finance: Wallet,
  sales: ShoppingCart,
  inventory: Package,
  hr: Users,
  manufacturing: Factory,
  crm: Briefcase,
  consulting: FolderKanban,
  portal: Boxes,
  appraisals: Briefcase,
}

function iconFor(app: AppEntry): LucideIcon {
  return ICON_BY_CODE[app.code] || SettingsIcon
}

export default function AppCatalog() {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['apps'],
    queryFn: async () => (await api.get<AppEntry[]>('/apps/')).data,
  })

  const toggleMut = useMutation({
    mutationFn: async ({ id, is_enabled }: { id: number; is_enabled: boolean }) =>
      (await api.patch(`/apps/${id}/`, { is_enabled })).data,
    onMutate: async ({ id, is_enabled }) => {
      await qc.cancelQueries({ queryKey: ['apps'] })
      const previous = qc.getQueryData<AppEntry[]>(['apps'])
      qc.setQueryData<AppEntry[]>(['apps'], (old) =>
        (old || []).map((a) => (a.id === id ? { ...a, is_enabled } : a))
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['apps'], ctx.previous)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['apps'] })
      // Also invalidate the shared enabled-apps cache so the sidebar and
      // route guards pick up the new state immediately.
      qc.invalidateQueries({ queryKey: ['my-apps'] })
    },
  })

  const apps = data || []

  return (
    <AdminLayout>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Modules</h2>
      <p className="text-sm text-gray-500 mb-6">
        Activate or deactivate ERP modules. When a module is disabled it is hidden
        from the sidebar and its pages are unreachable for both end users and admins.
        Re-enable it here to restore access.
      </p>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {apps.map((app) => {
            const Icon = iconFor(app)
            return (
              <div
                key={app.id}
                className="bg-card rounded-lg shadow p-5 border border-border transition"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="rounded-md bg-primary/10 text-primary p-2">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{app.name}</h3>
                      <p className="text-xs text-gray-400 font-mono">{app.code}</p>
                    </div>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded font-semibold ${statusBadgeStyles[app.status]}`}
                  >
                    {app.status}
                  </span>
                </div>

                <p className="text-sm text-gray-600 mb-4 min-h-[3em]">{app.description}</p>

                <div className="flex items-center justify-between">
                  <Toggle
                    checked={app.is_enabled}
                    disabled={app.status === 'PLANNED' || toggleMut.isPending}
                    onChange={(v) => toggleMut.mutate({ id: app.id, is_enabled: v })}
                    label={app.is_enabled ? 'Enabled' : 'Disabled'}
                  />
                  {app.status === 'PLANNED' && (
                    <span className="text-xs text-gray-400">Coming soon</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </AdminLayout>
  )
}

function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
  label?: string
}) {
  return (
    <label className={`inline-flex items-center cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
          checked ? 'bg-blue-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
      {label && <span className="ml-2 text-sm text-gray-700">{label}</span>}
    </label>
  )
}
